import React, { useEffect, useState } from 'react';
import './PatientDetail.css';
import { IoClose } from 'react-icons/io5';
import { useParams } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import AssignLawyerPhysician from '../../ui/panels/Forms/AssignForm/AssignForm';
import CaseDocumentUploadPage from '../../adminPages/CaseDocumentUpload/CaseDocumentUpload.page';

interface Patient {
  entityId: string;
  firstName: string;
  lastName: string;
  email: string;
  status: string;
  patientAssignee: {
    lawyers: Array<{
      firstName: string;
      lastName: string;
      userEntityAccessId: string;
    }>;
    physicians: Array<{
      firstName: string;
      lastName: string;
      userEntityAccessId: string;
    }>;
  };
}

interface AssignUserList {
  userId: string;
  firstName: string;
  lastName: string;
}

function PatientDetailPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const [patientData, setPatientData] = useState<Patient>({
    entityId: '',
    firstName: '',
    lastName: '',
    email: '',
    status: '',
    patientAssignee: {
      lawyers: [],
      physicians: [],
    },
  });

  const [uploadedFiles, setUploadedFiles] = useState<any[]>([]);
  const [lawyers, setLawyers] = useState<AssignUserList[]>([]);
  const [physicians, setPhysicians] = useState<AssignUserList[]>([]);

  const nav = useNavigate();
  const [token, setToken] = useState<string>(
    localStorage.getItem('token') || ''
  );

  const [showUploadFileForm, setShowUploadFileForm] = useState<boolean>(false);

  const [showAssignForm, setShowAssignForm] = useState(false);
  const [error, setError] = useState<string>('');

  const fetchPatientData = async () => {
    if (token == '') {
      nav('/signin');
    }

    if (!id) {
      setError('No patient ID provided');
      return;
    }

    try {
      const url = `/admin/caseDetails/${id}?detailed=true`;
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch patient data');
      }

      const data = await response.json();
      const formattedPatientData: Patient = data;
      setPatientData(formattedPatientData);
    } catch (error) {
      console.log('Error while fetching user list data');
      console.log(error);
    }
  };

  const fetchPatientUploadedFiles = async () => {
    if (!patientData.entityId) {
      console.log('EntityId not found.');
      return;
    }

    try {
      const url = '/case/upload/jobs';
      const jsonData = { entityId: parseInt(patientData.entityId) };
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(jsonData),
      });

      if (!response.ok) {
        throw new Error('Failed to fetch patient uploaded files');
      }
      const data = await response.json();
      setUploadedFiles(data);
    } catch (error) {
      console.log('Error in fetching the patient uploaded files list');
      console.log(error);
    }
  };

  const fetchAssignableUsers = async () => {
    try {
      // Fetching Lawyers
      const lawyerUrl = '/admin/getUsers/2/1'; // 2 is the hardcoded value for lawyer and 1 for pagination
      const resLawyers = await fetch(lawyerUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      if (!resLawyers.ok) {
        throw new Error('Failed to fetch lawyers in fetch assignable');
      }

      const lawyerList = await resLawyers.json();
      setLawyers(lawyerList.users);

      // Fetching Physicians
      const physicianUrl = '/admin/getUsers/3/1'; // 3 is the hardcoded value for physician and 1 for pagination
      const resPhysicians = await fetch(physicianUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      if (!resPhysicians.ok) {
        throw new Error('Failed to fetch physicians in fetch assignable');
      }

      const physicianList = await resPhysicians.json();
      setPhysicians(physicianList.users);
    } catch (error) {
      console.log(
        'An error occurred while fetching lawyer and physician lists.'
      );
      console.log(error);
    }
  };

  useEffect(() => {
    fetchPatientData();
    fetchAssignableUsers();
  }, [id]);

  useEffect(() => {
    if (patientData.entityId) {
      console.log(`fetching patient files`);
      fetchPatientUploadedFiles();
    }
  }, [patientData.entityId]);

  if (!patientData) {
    return <div>No patient data available</div>; //TODO: Need to add a better UI for no patient data found
  }

  const handleOpenAssignBox = () => {
    setShowAssignForm(true);
  };

  const handleCloseAssignBox = () => {
    setShowAssignForm(false);
  };

  const handleUploadFileForm = () => {
    setShowUploadFileForm(true);
  };

  const handleCloseUploadFileForm = () => {
    setShowUploadFileForm(false);
  };

  const requestNewAnswers = async () => {
    // loading indicator here
    try {
      const url = '/case/answers/jobs';
      const jsonData = {
        entityId: parseInt(patientData.entityId),
        patientLastName: patientData.lastName,
      };
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(jsonData),
      });

      if (!response.ok) {
        // error handling here
        return;
      }
    } catch (error) {
      console.log('Error in fetching the patient uploaded files list');
      console.log(error);
    }
    // end loading indicator
  };

  return (
    <div className="patient-detail-page">
      <div className="patient-detail-head">
        <h2>Patient Details</h2>
        <button>DOWNLOAD LETTER</button>
      </div>

      <div className="patient-detail-info-box">
        <div className="patient-detail-info">
          <p>
            Patient name:{' '}
            <span className="patient-detail-info-name">
              {patientData.firstName} {patientData.lastName}
            </span>
          </p>
          <p>
            Patient email:{' '}
            <span className="patient-detail-info-name">
              {patientData.email}
            </span>
          </p>
          <p>
            Status:{' '}
            <span className="patient-detail-info-status">
              {patientData.status}
            </span>
          </p>
          {/* <p>
            Case Status:{' '}
            <span className="patient-detail-info-case-status">IN REVIEW</span> //TODO: Need to check and then integrate the case status field in the API
          </p> */}
        </div>
        <div className="patient-detail-info-buttons">
          {/* <button  onClick={() => nav(`/case/upload/${patientData.entityId}`)}>UPLOAD FILES</button> */}
          <button onClick={() => handleUploadFileForm()}>UPLOAD FILES</button>
          <button onClick={() => handleOpenAssignBox()}>ASSIGN</button>
          <button onClick={() => requestNewAnswers()}>CREATE ANSWERS</button>
          <button onClick={() => nav(`/combined-report/${id}`)}>VIEW AND DOWNLOAD LETTER</button>
        </div>
      </div>

      <div className="patient-detail-files-assignees">
        <div className="patient-detail-uploaded-files">
          <h2>Uploaded Files</h2>
          {uploadedFiles && uploadedFiles.length > 0 ? (
            <div className="patient-detail-files">
              {uploadedFiles.map((file, index) => (
                <p key={index}>{file.title}</p>
              ))}{' '}
              {/* //TODO: Need to confirm the document details to show here. */}
            </div>
          ) : (
            <p>No Files uploaded yet.</p>
          )}
        </div>

        <div className="patient-detail-lawyers-assign">
          <h2>Lawyers</h2>
          {patientData.patientAssignee.lawyers.map((lawyer, index) => (
            <div
              key={lawyer.userEntityAccessId}
              className="patient-detail-lawyers"
            >
              <p>
                {lawyer.firstName} {lawyer.lastName}
              </p>
              <IoClose className="patient-detail-close-icon" />
            </div>
          ))}
        </div>

        <div className="patient-detail-physician-assign">
          <h2>Physicians</h2>
          {patientData.patientAssignee.physicians.map((physician, index) => (
            <div
              key={physician.userEntityAccessId}
              className="patient-detail-lawyers"
            >
              <p>
                {physician.firstName} {physician.lastName}
              </p>
              <IoClose className="patient-detail-close-icon" />
            </div>
          ))}
        </div>
      </div>

      {showAssignForm && (
        <AssignLawyerPhysician
          token={token}
          nav={nav}
          handleCloseFormPressed={handleCloseAssignBox}
          entityId={patientData.entityId}
          onSaveAssignedUser={() => {
            fetchPatientData();
          }}
          assignableLawyers={lawyers}
          assignablePhysicians={physicians}
        />
      )}

      {showUploadFileForm && (
        <CaseDocumentUploadPage
          token={token}
          nav={nav}
          entityId={patientData.entityId}
          entityFirstName={patientData.firstName}
          entityLastName={patientData.lastName}
          onClose={handleCloseUploadFileForm}
        />
      )}
    </div>
  );
}

export default PatientDetailPage;
